import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  isMultiSeriesQueryResponse,
  isSingleQueryResponse,
  type MultiSeriesQueryResponse,
  type ResponsiveLayoutConfig,
  type SingleQueryResponse,
  type UpdateWidgetsLayoutResponse,
  type WidgetListResponseData,
  type WidgetQueryRequest,
  type WidgetQueryResponse,
  type WidgetResponseData,
} from "@veritab/api-contracts";
import axios, { AxiosError } from "axios";

import { ApiError } from "@/shared/lib/errors";

import { widgetKeys } from "./keys";

// API client for widgets (Node.js API)
export const widgetApi = axios.create({
  baseURL: `${String(import.meta.env.VITE_CORE_API_URL)}/widgets`,
});

// Widget query response (core-api wraps the Python data identically and adds a
// Node-side branch for tally data sources).
interface WidgetQueryApiResponse {
  success: boolean;
  message?: string;
  data?: WidgetQueryResponse;
}

// API functions
export const getWidgetsByDashboard = async (dashboardId: string) => {
  try {
    const response = await widgetApi.get<WidgetListResponseData>("/", {
      params: { dashboardId },
      withCredentials: true,
    });
    const responseData = response.data;
    if (!responseData.success) {
      throw new Error(responseData.message);
    }
    return responseData.data;
  } catch (err) {
    if (err instanceof AxiosError) {
      throw new ApiError(err);
    }
    throw err;
  }
};

export const getWidgetById = async (widgetId: string) => {
  try {
    const response = await widgetApi.get<WidgetResponseData>(`/${widgetId}`, {
      withCredentials: true,
    });
    const responseData = response.data;
    if (!responseData.success) {
      throw new Error(responseData.message);
    }
    return responseData.data;
  } catch (err) {
    if (err instanceof AxiosError) {
      throw new ApiError(err);
    }
    throw err;
  }
};

// React Query hooks
export function useWidgetsByDashboardQuery(dashboardId: string | undefined) {
  return useQuery({
    queryKey: widgetKeys.list(dashboardId ?? ""),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => getWidgetsByDashboard(dashboardId!),
    enabled: !!dashboardId,
  });
}

export function useWidgetByIdQuery(widgetId: string) {
  return useQuery({
    queryKey: widgetKeys.detail(widgetId),
    queryFn: () => getWidgetById(widgetId),
    enabled: !!widgetId,
  });
}

/**
 * Execute a widget query.
 *
 * Returns either:
 * - SingleQueryResponse for KPI widgets (kpi-card, stat-card, metric-card)
 * - MultiSeriesQueryResponse for chart widgets (area-chart, line-chart)
 */
export const executeWidgetQuery = async (
  widgetId: string,
  request: Omit<WidgetQueryRequest, "widgetId">
): Promise<WidgetQueryResponse> => {
  try {
    // Call core-api, which routes to Python (parquet/s3_tables) or executes
    // Node-side against the org's Tally DB depending on the widget's dataSource.
    const response = await widgetApi.post<WidgetQueryApiResponse>(
      `/${widgetId}/query`,
      {
        dashboardId: request.dashboardId,
        parameters: request.parameters,
        limit: request.limit,
        offset: request.offset,
        queryExecutionId: request.queryExecutionId,
      },
      {
        withCredentials: true,
      }
    );

    // Response shape (identical to Python's):
    // - KPI widgets: { success: true, data: { data, columns, rowCount } }
    // - Chart widgets: { success: true, data: { results: [{ seriesId, data, columns }, ...] } }
    // - Table widgets: { success: true, data: { data, columns, rowCount, totalCount?, queryExecutionId? } }
    const responseData = response.data;
    if (!responseData.success || !responseData.data) {
      throw new Error(responseData.message ?? "Failed to execute widget query");
    }

    // Return the data as-is - it's already in the correct format
    return responseData.data;
  } catch (err) {
    if (err instanceof AxiosError) {
      throw new ApiError(err);
    }
    throw err;
  }
};

/**
 * Hook for executing widget queries.
 * Returns typed data based on widget type.
 */
export function useWidgetQuery(
  widgetId: string,
  dashboardId: string | null | undefined,
  parameters: Record<string, unknown> = {},
  options?: { limit?: number; offset?: number; queryExecutionId?: string }
) {
  return useQuery({
    // Note: queryExecutionId NOT in queryKey - we want same cache key for all pages
    // The queryExecutionId is passed to reuse Athena cached results
    queryKey: widgetKeys.query(widgetId, {
      dashboardId: dashboardId ?? null,
      parameters,
      limit: options?.limit,
      offset: options?.offset,
    }),
    queryFn: () =>
      executeWidgetQuery(widgetId, {
        dashboardId: dashboardId ?? null,
        parameters,
        limit: options?.limit,
        offset: options?.offset,
        queryExecutionId: options?.queryExecutionId,
      }),
    enabled: !!widgetId,
  });
}

// ============ Layout Update API ============

interface UpdateWidgetsLayoutRequest {
  dashboardId: string;
  layouts: Array<{
    widgetId: string;
    layout: ResponsiveLayoutConfig;
  }>;
}

interface UpdateWidgetsLayoutApiResponse {
  success: boolean;
  message?: string;
  data?: UpdateWidgetsLayoutResponse;
}

export const updateWidgetsLayout = async (
  request: UpdateWidgetsLayoutRequest
): Promise<UpdateWidgetsLayoutResponse> => {
  try {
    const response = await widgetApi.patch<UpdateWidgetsLayoutApiResponse>("/layout", request, {
      withCredentials: true,
    });
    const responseData = response.data;
    if (!responseData.success || !responseData.data) {
      throw new Error(responseData.message ?? "Failed to update widget layouts");
    }
    return responseData.data;
  } catch (err) {
    if (err instanceof AxiosError) {
      throw new ApiError(err);
    }
    throw err;
  }
};

/**
 * Hook for updating widget layouts
 */
export function useUpdateWidgetsLayoutMutation(dashboardId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (layouts: UpdateWidgetsLayoutRequest["layouts"]) =>
      updateWidgetsLayout({ dashboardId, layouts }),
    onSuccess: () => {
      // Invalidate widgets list query to refetch updated data
      void queryClient.invalidateQueries({ queryKey: widgetKeys.list(dashboardId) });
    },
  });
}

// ============ Delete Widget API ============

interface DeleteWidgetApiResponse {
  success: boolean;
  message?: string;
}

export const deleteWidget = async (widgetId: string): Promise<void> => {
  try {
    const response = await widgetApi.delete<DeleteWidgetApiResponse>(`/${widgetId}`, {
      withCredentials: true,
    });
    const responseData = response.data;
    if (!responseData.success) {
      throw new Error(responseData.message ?? "Failed to delete widget");
    }
  } catch (err) {
    if (err instanceof AxiosError) {
      throw new ApiError(err);
    }
    throw err;
  }
};

/**
 * Hook for deleting a widget
 */
export function useDeleteWidgetMutation(dashboardId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (widgetId: string) => deleteWidget(widgetId),
    onSuccess: () => {
      // Invalidate widgets list query to refetch updated data
      void queryClient.invalidateQueries({ queryKey: widgetKeys.list(dashboardId) });
    },
  });
}

// Re-export type guards for convenience
export { isMultiSeriesQueryResponse, isSingleQueryResponse };

// Re-export response types for convenience
export type { MultiSeriesQueryResponse, SingleQueryResponse, WidgetQueryResponse };
